'use client';
/* eslint-disable @next/next/no-img-element */

import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';

import { trackClientEvent } from '@/lib/analytics';
import type { ExperienceEntry, Project, Scene } from '@/lib/schemas';

type ArExperiencePlayerProps = {
  project: Project;
  entry: ExperienceEntry;
  scenes: Scene[];
};

type ExperiencePhase = 'intro' | 'scanning' | 'playing' | 'fallback';

declare global {
  interface Window {
    AFRAME?: {
      components?: Record<string, unknown>;
    };
    dataLayer?: unknown[];
  }
}

function vectorToString(vector: [number, number, number]) {
  return `${vector[0]} ${vector[1]} ${vector[2]}`;
}

function offsetVector(vector: [number, number, number], zOffset: number): [number, number, number] {
  return [vector[0], vector[1], vector[2] + zOffset];
}

function loadScriptOnce(src: string, isReady: () => boolean) {
  if (isReady()) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(`script[data-runtime-src="${src}"]`) as
      | HTMLScriptElement
      | null;

    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {
        once: true
      });
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.runtimeSrc = src;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {
      once: true
    });
    document.head.appendChild(script);
  });
}

export function ArExperiencePlayer({ project, entry, scenes }: ArExperiencePlayerProps) {
  const sceneRef = useRef<HTMLElement | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const startInFlightRef = useRef(false);
  const [scriptsReady, setScriptsReady] = useState(false);
  const [phase, setPhase] = useState<ExperiencePhase>('intro');
  const [statusMessage, setStatusMessage] = useState('Chargement du moteur AR...');
  const [activeSceneId, setActiveSceneId] = useState<string | null>(null);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [showAltContent, setShowAltContent] = useState(false);
  const [manualPlaybackSceneId, setManualPlaybackSceneId] = useState<string | null>(null);
  const [fallbackReason, setFallbackReason] = useState<string | null>(null);

  const sceneMap = useMemo(
    () => new Map(scenes.map((scene) => [scene.id, scene])),
    [scenes]
  );
  const currentScene = (activeSceneId ? sceneMap.get(activeSceneId) : null) ?? scenes[0];
  const longestTimeout = Math.max(...scenes.map((scene) => scene.trackingProfile.detectionTimeoutMs));

  const mindarAttributes = useMemo(
    () =>
      [
        `imageTargetSrc: ${entry.mindFile}`,
        `maxTrack: ${entry.maxTrack}`,
        'uiLoading: no',
        'uiScanning: no',
        'filterMinCF: 0.0001',
        'filterBeta: 0.001',
        'missTolerance: 5',
        'warmupTolerance: 5',
        'autoStart: false'
      ].join('; '),
    [entry.maxTrack, entry.mindFile]
  );

  useEffect(() => {
    let cancelled = false;

    async function bootstrapRuntime() {
      try {
        await loadScriptOnce('/vendor/aframe.min.js', () => Boolean(window.AFRAME));
        await loadScriptOnce('/vendor/mindar-image-aframe.prod.js', () =>
          Boolean(window.AFRAME?.components?.['mindar-image'])
        );
        if (!cancelled) {
          setScriptsReady(true);
          setStatusMessage('Le moteur AR est pret. Demarre l’experience.');
        }
      } catch (error) {
        if (!cancelled) {
          setStatusMessage('Impossible de charger le moteur AR.');
          setPhase('fallback');
          setFallbackReason('runtime-error');
        }
      }
    }

    bootstrapRuntime();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!scriptsReady || !sceneRef.current) {
      return;
    }

    const sceneElement = sceneRef.current;
    const anchorElements = Array.from(
      sceneElement.querySelectorAll<HTMLElement>('[data-scene-id]')
    );

    const clearDetectionTimeout = () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const scheduleDetectionTimeout = () => {
      clearDetectionTimeout();
      timeoutRef.current = window.setTimeout(() => {
        setPhase('fallback');
        setFallbackReason('tracking-timeout');
        setStatusMessage(
          "Aucune scene detectee pour l'instant. Tu peux relancer la camera ou ouvrir le contenu alternatif."
        );
        trackClientEvent('fallback_timeout', { entry: entry.slug });
      }, longestTimeout);
    };

    const handleArReady = () => {
      setStatusMessage("Camera active. Vise la bache ou l'un des visuels de l'experience.");
      if (phase === 'scanning') {
        scheduleDetectionTimeout();
      }
    };

    const handleArError = () => {
      clearDetectionTimeout();
      setPhase('fallback');
      setFallbackReason('ar-error');
      setStatusMessage("Le moteur AR a rencontre une erreur. Verifie la camera et relance l'experience.");
      trackClientEvent('ar_error', { entry: entry.slug });
    };

    const playSceneVideo = async (scene: Scene) => {
      const video = document.getElementById(`video-${scene.id}`) as HTMLVideoElement | null;
      if (!video || scene.arContent.kind !== 'video') {
        return;
      }

      video.currentTime = 0;
      video.loop = scene.arContent.loop;
      video.playsInline = true;
      video.muted = scene.arContent.withAudio ? !audioUnlocked : true;

      try {
        await video.play();
        setManualPlaybackSceneId(null);
        trackClientEvent('video_playing', {
          entry: entry.slug,
          scene: scene.slug,
          withAudio: scene.arContent.withAudio
        });
      } catch {
        setManualPlaybackSceneId(scene.id);
        setStatusMessage(
          "La scene est detectee mais le navigateur a bloque la lecture audio. Utilise 'Lancer le son'."
        );
        trackClientEvent('video_play_blocked', { entry: entry.slug, scene: scene.slug });
      }
    };

    const pauseSceneVideo = (scene: Scene) => {
      const video = document.getElementById(`video-${scene.id}`) as HTMLVideoElement | null;
      if (!video || scene.arContent.kind !== 'video') {
        return;
      }
      video.pause();
      video.currentTime = 0;
    };

    const handleTargetFound = async (sceneId: string) => {
      clearDetectionTimeout();
      const scene = sceneMap.get(sceneId);
      if (!scene) return;

      setActiveSceneId(sceneId);
      setPhase('playing');
      setFallbackReason(null);
      setShowAltContent(false);
      setStatusMessage(`Scene detectee : ${scene.title}`);
      trackClientEvent('target_found', { entry: entry.slug, scene: scene.slug });

      if (navigator.vibrate) {
        navigator.vibrate(40);
      }

      await playSceneVideo(scene);
    };

    const handleTargetLost = (sceneId: string) => {
      const scene = sceneMap.get(sceneId);
      if (!scene) return;

      pauseSceneVideo(scene);
      if (activeSceneId === sceneId) {
        setActiveSceneId(null);
        setPhase('scanning');
        setStatusMessage("Scene perdue. Recadre l'image pour relancer le contenu.");
        scheduleDetectionTimeout();
      }
      trackClientEvent('target_lost', { entry: entry.slug, scene: scene.slug });
    };

    sceneElement.addEventListener('arReady', handleArReady);
    sceneElement.addEventListener('arError', handleArError);

    const cleanupFns = anchorElements.map((anchor) => {
      const sceneId = anchor.dataset.sceneId;
      if (!sceneId) return () => undefined;

      const onFound = () => void handleTargetFound(sceneId);
      const onLost = () => handleTargetLost(sceneId);
      anchor.addEventListener('targetFound', onFound);
      anchor.addEventListener('targetLost', onLost);

      return () => {
        anchor.removeEventListener('targetFound', onFound);
        anchor.removeEventListener('targetLost', onLost);
      };
    });

    return () => {
      clearDetectionTimeout();
      sceneElement.removeEventListener('arReady', handleArReady);
      sceneElement.removeEventListener('arError', handleArError);
      cleanupFns.forEach((cleanup) => cleanup());
    };
  }, [activeSceneId, audioUnlocked, entry.slug, longestTimeout, phase, sceneMap, scriptsReady]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }

      const arSystem = (sceneRef.current as unknown as { systems?: Record<string, { stop: () => void }> })
        ?.systems?.['mindar-image-system'];
      if (arSystem) {
        arSystem.stop();
      }
    };
  }, []);

  async function primeMediaInGesture() {
    const videoElements = Array.from(
      document.querySelectorAll<HTMLVideoElement>('video[data-scene-video="true"]')
    );
    let unlocked = false;

    for (const video of videoElements) {
      const wantsAudio = video.dataset.withAudio === 'true';
      try {
        video.currentTime = 0;
        video.muted = !wantsAudio;
        await video.play();
        video.pause();
        video.currentTime = 0;
        if (wantsAudio) {
          unlocked = true;
        }
      } catch {
        if (!wantsAudio) {
          video.muted = true;
        }
      }
    }

    setAudioUnlocked(unlocked || videoElements.every((video) => video.dataset.withAudio !== 'true'));
  }

  async function startExperience() {
    if (startInFlightRef.current) {
      return;
    }

    const arSystem = (sceneRef.current as unknown as {
      systems?: Record<string, { start: () => Promise<void> }>;
    })?.systems?.['mindar-image-system'];

    if (!scriptsReady || !sceneRef.current || !arSystem) {
      setStatusMessage('Le moteur AR se prepare encore. Reessaie dans une seconde.');
      return;
    }

    startInFlightRef.current = true;
    setStatusMessage('Demande d’acces camera...');

    try {
      await primeMediaInGesture();
      await arSystem.start();
      setPhase('scanning');
      setActiveSceneId(null);
      setShowAltContent(false);
      setFallbackReason(null);
      setStatusMessage(currentScene.trackingProfile.instruction);
      trackClientEvent('experience_started', {
        entry: entry.slug,
        mode: entry.mode,
        scenes: scenes.length
      });
    } catch {
      setPhase('fallback');
      setFallbackReason('camera-start');
      setStatusMessage("Impossible d'activer la camera. Verifie Safari/Chrome mobile et l'autorisation camera.");
      trackClientEvent('experience_start_failed', { entry: entry.slug });
    } finally {
      startInFlightRef.current = false;
    }
  }

  async function reactivateCamera() {
    const arSystem = (sceneRef.current as unknown as {
      systems?: Record<string, { stop: () => void; start: () => Promise<void> }>;
    })?.systems?.['mindar-image-system'];

    if (!arSystem) {
      setStatusMessage('Le systeme AR nest pas disponible pour le moment.');
      return;
    }

    arSystem.stop();
    setActiveSceneId(null);
    setManualPlaybackSceneId(null);
    setPhase('intro');
    setStatusMessage('Camera coupee. Tu peux relancer l’experience.');
    trackClientEvent('camera_stopped', { entry: entry.slug });
  }

  async function replayCurrentScene() {
    if (!currentScene) return;
    if (currentScene.arContent.kind !== 'video') return;

    const video = document.getElementById(`video-${currentScene.id}`) as HTMLVideoElement | null;
    if (!video) return;

    try {
      video.currentTime = 0;
      await video.play();
      setManualPlaybackSceneId(null);
      setStatusMessage(`Lecture relancee : ${currentScene.title}`);
      trackClientEvent('scene_replayed', { entry: entry.slug, scene: currentScene.slug });
    } catch {
      setManualPlaybackSceneId(currentScene.id);
      setStatusMessage("Le navigateur demande une action supplementaire pour relancer la lecture.");
    }
  }

  async function launchManualPlayback() {
    if (!manualPlaybackSceneId) {
      return;
    }

    const scene = sceneMap.get(manualPlaybackSceneId);
    const video = document.getElementById(`video-${manualPlaybackSceneId}`) as HTMLVideoElement | null;
    if (!scene || !video || scene.arContent.kind !== 'video') {
      return;
    }

    try {
      video.muted = false;
      await video.play();
      setManualPlaybackSceneId(null);
      setAudioUnlocked(true);
      setStatusMessage(`Audio active : ${scene.title}`);
      trackClientEvent('manual_audio_started', { entry: entry.slug, scene: scene.slug });
    } catch {
      setStatusMessage("Le son reste bloque sur ce navigateur. Le contenu alternatif est disponible.");
    }
  }

  return (
    <div className="ar-player">
      <div className="ar-player__ui">
        <section className="panel experience-panel">
          <div className="experience-panel__header">
            <div>
              <p className="eyebrow">Experience</p>
              <h1>{entry.introTitle}</h1>
            </div>
            <span className="pill">{entry.mode === 'single' ? 'Mono-scene' : 'Multi-scenes'}</span>
          </div>

          <p className="lead">{entry.introBody}</p>

          <div className="status-chip">
            <strong>Etat</strong>
            <span>{statusMessage}</span>
          </div>

          <div className="meta-tags">
            <span>{scenes.length} scene(s)</span>
            <span>maxTrack {entry.maxTrack}</span>
            <span>{audioUnlocked ? 'Audio pret' : 'Audio a debloquer'}</span>
          </div>

          <div className="button-row">
            <button className="button button--primary" type="button" onClick={startExperience}>
              {phase === 'intro' ? 'Demarrer l’experience' : 'Relancer la camera'}
            </button>
            <button className="button button--secondary" type="button" onClick={reactivateCamera}>
              Couper / reinitialiser
            </button>
            {currentScene?.arContent.kind === 'video' ? (
              <button className="button button--secondary" type="button" onClick={replayCurrentScene}>
                Rejouer
              </button>
            ) : null}
            {manualPlaybackSceneId ? (
              <button className="button button--primary" type="button" onClick={launchManualPlayback}>
                Lancer le son
              </button>
            ) : null}
            <button
              className="button button--ghost"
              type="button"
              onClick={() => setShowAltContent((value) => !value)}
            >
              {showAltContent ? 'Masquer le fallback' : 'Voir le contenu alternatif'}
            </button>
          </div>

          <div className="instruction-list">
            <p className="eyebrow">Parcours</p>
            <ul>
              <li>1 tap pour debloquer camera et audio.</li>
              <li>Vise la bache en entier, sans couper le visuel.</li>
              <li>Si rien ne se passe, ouvre le fallback puis rejoue.</li>
            </ul>
          </div>
        </section>

        <section className="panel scene-selector">
          <div className="scene-selector__header">
            <div>
              <p className="eyebrow">Scenes reliees</p>
              <h2>Catalogue de l’entree</h2>
            </div>
            {fallbackReason ? <span className="pill pill--warning">{fallbackReason}</span> : null}
          </div>
          <div className="scene-selector__list">
            {scenes.map((scene) => (
              <article
                key={scene.id}
                className={`scene-selector__item ${scene.id === currentScene.id ? 'is-active' : ''}`}
              >
                <div>
                  <strong>{scene.title}</strong>
                  <p>{scene.trackingProfile.instruction}</p>
                </div>
                <span>target #{scene.targetIndex}</span>
              </article>
            ))}
          </div>
          {showAltContent ? (
            <div className="fallback-card">
              <p className="eyebrow">Fallback</p>
              <h3>{currentScene.fallback.title}</h3>
              <p className="copy">{currentScene.fallback.body}</p>
              {currentScene.fallback.altContentType === 'image' ? (
                <img
                  src={currentScene.fallback.altContentSrc}
                  alt={`Fallback ${currentScene.title}`}
                  className="fallback-card__media"
                />
              ) : null}
              {currentScene.fallback.altContentType === 'video' ? (
                <video
                  className="fallback-card__media"
                  controls
                  loop
                  playsInline
                  preload="metadata"
                  src={currentScene.fallback.altContentSrc}
                />
              ) : null}
              {currentScene.fallback.altContentType === 'link' ? (
                <Link href={currentScene.fallback.altContentSrc} className="button button--secondary">
                  Ouvrir la scene classique
                </Link>
              ) : null}
            </div>
          ) : null}
        </section>
      </div>

      <div className="ar-scene-shell">
        {!scriptsReady ? (
          <div className="runtime-loader panel">
            <p className="eyebrow">Chargement</p>
            <h2>Mise en place du moteur AR</h2>
            <p className="copy">
              Les scripts A-Frame et MindAR sont charges uniquement sur cette route pour garder
              le reste du site leger.
            </p>
          </div>
        ) : null}

        <a-scene
          ref={(node: HTMLElement | null) => {
            sceneRef.current = node;
          }}
          mindar-image={mindarAttributes}
          embedded
          color-space="sRGB"
          renderer="colorManagement: true, physicallyCorrectLights"
          vr-mode-ui="enabled: false"
          device-orientation-permission-ui="enabled: false"
          className="ar-scene"
        >
          <a-assets timeout="12000">
            {scenes.map((scene) =>
              scene.arContent.kind === 'video' ? (
                <video
                  id={`video-${scene.id}`}
                  key={scene.id}
                  data-scene-video="true"
                  data-with-audio={String(scene.arContent.withAudio)}
                  src={scene.arContent.src}
                  preload="auto"
                  playsInline
                  loop={scene.arContent.loop}
                  muted={!scene.arContent.withAudio}
                  poster={scene.arContent.poster}
                  crossOrigin="anonymous"
                />
              ) : scene.arContent.kind === 'image' ? (
                <img
                  id={`image-${scene.id}`}
                  key={scene.id}
                  src={scene.arContent.src}
                  alt={scene.title}
                  crossOrigin="anonymous"
                />
              ) : null
            )}
          </a-assets>

          <a-camera position="0 0 0" look-controls="enabled: false" />

          {scenes.map((scene) => {
            const guideWidth = scene.arContent.width * scene.trackingProfile.guideScale;
            const guideHeight = scene.arContent.height * scene.trackingProfile.guideScale;
            const guidePosition = offsetVector(scene.arContent.position, -0.002);

            return (
              <a-entity
                key={scene.id}
                data-scene-id={scene.id}
                mindar-image-target={`targetIndex: ${scene.targetIndex}`}
              >
                <a-plane
                  width={guideWidth}
                  height={guideHeight}
                  position={vectorToString(guidePosition)}
                  rotation={vectorToString(scene.arContent.rotation)}
                  material={`color: ${project.theme.accent}; opacity: 0.18; shader: flat; side: double; transparent: true;`}
                />

                {scene.arContent.kind === 'video' ? (
                  <a-video
                    src={`#video-${scene.id}`}
                    width={scene.arContent.width}
                    height={scene.arContent.height}
                    position={vectorToString(scene.arContent.position)}
                    rotation={vectorToString(scene.arContent.rotation)}
                    scale={vectorToString(scene.arContent.scale)}
                    material="shader: flat; side: double; transparent: true;"
                  />
                ) : null}

                {scene.arContent.kind === 'image' ? (
                  <a-image
                    src={`#image-${scene.id}`}
                    width={scene.arContent.width}
                    height={scene.arContent.height}
                    position={vectorToString(scene.arContent.position)}
                    rotation={vectorToString(scene.arContent.rotation)}
                    scale={vectorToString(scene.arContent.scale)}
                    material="shader: flat; side: double; transparent: true;"
                  />
                ) : null}

                {scene.arContent.kind === 'html2d' ? (
                  <>
                    <a-plane
                      width={scene.arContent.width}
                      height={scene.arContent.height}
                      position={vectorToString(scene.arContent.position)}
                      rotation={vectorToString(scene.arContent.rotation)}
                      scale={vectorToString(scene.arContent.scale)}
                      material={`color: ${scene.arContent.theme.background}; opacity: 0.95; shader: flat; side: double;`}
                    />
                    <a-plane
                      width={scene.arContent.width}
                      height={0.06}
                      position={vectorToString([
                        scene.arContent.position[0],
                        scene.arContent.position[1] + scene.arContent.height / 2 - 0.08,
                        scene.arContent.position[2] + 0.001
                      ])}
                      rotation={vectorToString(scene.arContent.rotation)}
                      material={`color: ${scene.arContent.theme.accent}; opacity: 1; shader: flat; side: double;`}
                    />
                    <a-text
                      value={scene.arContent.headline}
                      align="center"
                      color={scene.arContent.theme.foreground}
                      width="1.8"
                      wrap-count="20"
                      position={vectorToString([
                        scene.arContent.position[0],
                        scene.arContent.position[1] + 0.13,
                        scene.arContent.position[2] + 0.002
                      ])}
                    />
                    <a-text
                      value={`${scene.arContent.body}${scene.arContent.cta ? `\n\n${scene.arContent.cta}` : ''}`}
                      align="center"
                      color={scene.arContent.theme.foreground}
                      width="1.6"
                      wrap-count="28"
                      position={vectorToString([
                        scene.arContent.position[0],
                        scene.arContent.position[1] - 0.08,
                        scene.arContent.position[2] + 0.002
                      ])}
                    />
                  </>
                ) : null}
              </a-entity>
            );
          })}
        </a-scene>
      </div>
    </div>
  );
}
